<template>
  <div class="page-wrapper">
    <div class="content container-fluid agent-v2-page waajo-agent-v2">
      <div class="agent-v2-heading">
        <div>
          <router-link to="/agent-ai-v2" class="agent-v2-back">
            <i class="fa fa-arrow-left"></i> Kembali ke daftar agent
          </router-link>
          <h3>{{ isEdit ? "Edit Agent AI & Chatbot" : "Atur Agent AI & Chatbot" }}</h3>
          <p>Atur cara AI atau Chatbot merespons pesan WhatsApp untuk satu lisensi.</p>
        </div>
        <span class="agent-v2-badge"><i class="fa fa-magic"></i> V2</span>
      </div>

      <div class="agent-stepper">
        <div
          v-for="item in steps"
          :key="item.number"
          class="agent-step"
          :class="{ active: step === item.number, done: step > item.number }"
        >
          <span class="agent-step-number">
            <i v-if="step > item.number" class="fa fa-check"></i>
            <template v-else>{{ item.number }}</template>
          </span>
          <div>
            <strong>{{ item.title }}</strong>
            <small>{{ item.subtitle }}</small>
          </div>
        </div>
      </div>

      <div v-if="loading" class="agent-v2-state">
        <i class="fa fa-spinner fa-spin"></i>
        <strong>Memuat data...</strong>
      </div>

      <div v-else-if="loadError" class="agent-v2-state is-error">
        <i class="fa fa-exclamation-circle"></i>
        <strong>{{ loadError }}</strong>
        <button type="button" class="btn btn-outline-primary" @click="loadInitial">Coba lagi</button>
      </div>

      <template v-else>
        <div v-if="wizardError" class="agent-alert is-danger">
          <i class="fa fa-exclamation-circle"></i>
          <span>{{ wizardError }}</span>
        </div>

        <div v-if="validationErrors.length" class="agent-validation-summary">
          <strong>Periksa kembali pengaturan berikut:</strong>
          <button
            v-for="(item, index) in validationErrors"
            :key="'validation-' + index"
            type="button"
            class="agent-validation-item"
            @click="focusValidationError(item)"
          >
            <span>{{ item.modeLabel || "Pengaturan" }}</span>
            {{ item.message }}
          </button>
        </div>

        <!-- Step 1 -->
        <section v-show="step === 1" class="agent-v2-card">
          <div class="agent-card-head">
            <div>
              <span class="agent-card-kicker">Tahap 1</span>
              <h4>Pilih Lisensi WhatsApp</h4>
              <p>Setiap lisensi memiliki pengaturan agent sendiri. Pilih nomor yang ingin diatur.</p>
            </div>
          </div>

          <div class="agent-license-picker">
            <label>Lisensi WhatsApp</label>
            <multiselect
              v-model="selectedLicense"
              :options="licenses"
              :multiple="false"
              :searchable="true"
              :close-on-select="true"
              :clear-on-select="false"
              :show-labels="false"
              label="license_name"
              track-by="id"
              placeholder="Cari nama atau nomor lisensi"
              :custom-label="licenseLabel"
              :loading="licensesLoading"
              @input="onLicenseChange"
            >
              <template slot="option" slot-scope="{ option }">
                <div class="agent-license-option">
                  <span>
                    <strong>{{ option.license_name || "Tanpa nama" }}</strong>
                    <small>{{ option.license_phone_number || "-" }}</small>
                  </span>
                  <span class="agent-connection" :class="Number(option.is_online_status) === 1 ? 'is-online' : 'is-offline'">
                    <i class="fa fa-circle"></i>
                    {{ Number(option.is_online_status) === 1 ? "Online" : "Offline" }}
                  </span>
                </div>
              </template>
            </multiselect>
            <div v-if="selectedLicense" class="agent-selected-license">
              <span class="agent-connection" :class="Number(selectedLicense.is_online_status) === 1 ? 'is-online' : 'is-offline'">
                <i class="fa fa-circle"></i>
                {{ Number(selectedLicense.is_online_status) === 1 ? "Online" : "Offline" }}
              </span>
              <span>{{ selectedLicense.license_name || "Tanpa nama" }}</span>
              <strong>{{ selectedLicense.license_phone_number || "-" }}</strong>
            </div>
            <div class="agent-info-note">
              <i class="fa fa-info-circle"></i>
              Pilih lisensi WhatsApp yang ingin Anda atur.
            </div>
          </div>
        </section>

        <!-- Step 2 -->
        <section v-show="step === 2" class="agent-v2-card">
          <div class="agent-card-head">
            <div>
              <span class="agent-card-kicker">Tahap 2</span>
              <h4>Pilih Mode Respons</h4>
              <p>Tentukan apakah agent menangani seluruh hari atau mengikuti jam kerja.</p>
            </div>
          </div>

          <div class="agent-mode-grid">
            <button
              v-for="option in modeOptions"
              :key="option.value"
              type="button"
              class="agent-mode-card"
              :class="{ active: selectedMode === option.value }"
              @click="onModeChange(option.value)"
            >
              <span class="agent-mode-icon"><i :class="option.icon"></i></span>
              <span class="agent-radio" :class="{ checked: selectedMode === option.value }"></span>
              <strong>{{ option.label }}</strong>
              <small>{{ option.description }}</small>
              <span v-if="option.value === 'schedule'" class="agent-mode-sub">
                Saat Jam Kerja + Di Luar Jam Kerja
              </span>
            </button>
          </div>

          <div v-if="isScheduleMode" class="agent-working-hours" data-agent-field="workingHours">
            <div class="agent-working-hours-head">
              <div>
                <span class="agent-card-kicker">Jadwal Operasional</span>
                <h5>Jam Operasional</h5>
                <p>Jadwal ini dipakai bersama oleh mode Saat Jam Kerja dan Di Luar Jam Kerja.</p>
              </div>
              <span
                class="agent-working-hours-badge"
                :class="{
                  'is-valid': workingHoursReadiness.isValid,
                  'is-invalid': !workingHoursLoading && !workingHoursReadiness.isValid
                }"
              >
                <i
                  :class="workingHoursLoading
                    ? 'fa fa-spinner fa-spin'
                    : (workingHoursReadiness.isValid ? 'fa fa-check-circle' : 'fa fa-exclamation-circle')"
                ></i>
                {{ workingHoursLoading ? "Memeriksa" : (workingHoursReadiness.isValid ? "Siap digunakan" : "Belum siap") }}
              </span>
            </div>

            <div v-if="workingHoursLoading" class="agent-working-hours-state">
              <i class="fa fa-spinner fa-spin"></i>
              Memeriksa jadwal jam kerja...
            </div>

            <div v-else-if="workingHoursError" class="agent-working-hours-alert is-error">
              <i class="fa fa-exclamation-circle"></i>
              <div>
                <strong>Jam kerja belum berhasil dimuat</strong>
                <p>{{ workingHoursError }}</p>
                <div class="agent-working-hours-actions">
                  <button type="button" class="btn btn-outline-secondary btn-sm" @click="loadWorkingHours(true)">
                    <i class="fa fa-refresh"></i> Coba lagi
                  </button>
                  <button type="button" class="btn agent-btn-primary btn-sm" @click="openWorkingHours">
                    <i class="fa fa-clock-o"></i> Atur Jam Kerja
                  </button>
                </div>
              </div>
            </div>

            <div v-else-if="!workingHoursReadiness.isValid" class="agent-working-hours-alert is-warning">
              <i class="fa fa-exclamation-triangle"></i>
              <div>
                <strong>{{ workingHoursReadiness.isConfigured ? "Jam kerja belum valid" : "Jam kerja belum diatur" }}</strong>
                <p>
                  Atur hari dan jam operasional terlebih dahulu agar Waajo dapat menentukan kapan harus menggunakan
                  balasan saat jam kerja dan kapan menggunakan balasan di luar jam kerja.
                </p>
                <ul v-if="workingHoursReadiness.errors.length" class="agent-working-hours-errors">
                  <li v-for="(item, index) in workingHoursReadiness.errors.slice(0, 4)" :key="'wh-error-' + index">
                    {{ item }}
                  </li>
                </ul>
                <div class="agent-working-hours-actions">
                  <button type="button" class="btn btn-outline-secondary btn-sm" @click="loadWorkingHours(true)">
                    <i class="fa fa-refresh"></i> Periksa ulang
                  </button>
                  <button type="button" class="btn agent-btn-primary btn-sm" @click="openWorkingHours">
                    <i class="fa fa-clock-o"></i> Atur Jam Kerja
                  </button>
                </div>
              </div>
            </div>

            <template v-else>
              <div class="agent-working-hours-summary">
                <div v-for="row in workingHoursSummaryRows" :key="row.key" class="agent-working-hours-row">
                  <span>{{ row.label }}</span>
                  <strong :class="{ 'is-off': !row.active }">{{ row.text }}</strong>
                </div>
              </div>
              <div class="agent-working-hours-foot">
                <small>
                  <i class="fa fa-info-circle"></i>
                  Jadwal jam kerja disimpan per akun, mengikuti timezone akun, dan digunakan oleh semua lisensi pada akun ini.
                </small>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="openWorkingHours">
                  <i class="fa fa-pencil"></i> Ubah Jam Kerja
                </button>
              </div>
            </template>
          </div>

          <div class="agent-info-note">
            <i class="fa fa-info-circle"></i>
            Pengaturan 24 Jam tidak dapat aktif bersamaan dengan Saat Jam Kerja atau Di Luar Jam Kerja. Saat Jam Kerja dan Di Luar Jam Kerja boleh aktif bersamaan.
          </div>

          <div v-if="analysis.messages.length" class="agent-alert is-warning">
            <i class="fa fa-exclamation-triangle"></i>
            <div>
              <strong>Pengaturan yang sudah ada</strong>
              <div v-for="(message, index) in analysis.messages" :key="index">{{ message }}</div>
            </div>
          </div>

          <div v-if="modeConflictMessage" class="agent-alert is-warning">
            <i class="fa fa-exclamation-triangle"></i>
            <span>{{ modeConflictMessage }}</span>
          </div>

          <div v-if="existingConfigs.length" class="agent-existing-box">
            <div class="agent-existing-head">
              <strong>Pengaturan yang sudah ada</strong>
              <agent-button variant="primary" size="sm" icon="fa fa-book" @click="openKbModal">
                Kelola Knowledge Base
              </agent-button>
            </div>
            <div v-for="row in existingConfigs" :key="row.id" class="agent-existing-row">
              <span class="agent-mode-pill" :class="'is-' + modeClass(modeFromConfig(row))">
                {{ modeLabel(modeFromConfig(row)) }}
              </span>
              <span>{{ row.title || "Tanpa judul" }}</span>
              <span class="agent-status-pill" :class="Number(row.is_publish) === 1 ? 'is-active' : 'is-draft'">
                {{ Number(row.is_publish) === 1 ? "Aktif" : "Draft" }}
              </span>
              <span class="agent-existing-type">{{ agentLabel(row) }}</span>
            </div>
          </div>
        </section>

        <!-- Step 3 -->
        <section v-show="step === 3" class="agent-v2-card">
          <div class="agent-card-head">
            <div>
              <span class="agent-card-kicker">Tahap 3</span>
              <h4>Pengaturan Agent</h4>
              <p>Pilih jenis agent untuk setiap mode. Saat Jam Kerja dan Di Luar Jam Kerja dapat memakai jenis berbeda.</p>
            </div>
          </div>

          <div v-if="aiBalance && aiBalance.is_available === false" class="agent-alert is-warning">
            <i class="fa fa-exclamation-triangle"></i>
            <span>Chat AI belum dapat digunakan pada akun ini. Hubungi admin atau aktifkan fitur AI terlebih dahulu.</span>
          </div>

          <div class="agent-agent-grid" :class="{ 'is-single': !isScheduleMode }">
            <div
              v-for="item in selectionFormItems"
              :key="item.key"
              class="agent-agent-card"
              :class="{ 'is-disabled': !item.form.enabled }"
            >
              <div class="agent-agent-card-head">
                <div>
                  <span class="agent-card-kicker">{{ item.modeLabel }}</span>
                  <h5>{{ item.modeLabel }}</h5>
                </div>
                <div class="agent-agent-card-head-actions">
                  <label v-if="isScheduleMode && !item.form.header.id" class="agent-include-toggle">
                    <span>{{ item.form.enabled ? "Disertakan" : "Tidak disertakan" }}</span>
                    <toggle-switch
                      :value="item.form.enabled"
                      aria-label="Disertakan"
                      @input="item.form.enabled = $event"
                    />
                  </label>
                  <span v-if="item.form.header.id" class="agent-status-pill" :class="Number(item.form.header.is_publish) === 1 ? 'is-active' : 'is-draft'">
                    {{ Number(item.form.header.is_publish) === 1 ? "Aktif" : "Draft" }}
                  </span>
                </div>
              </div>

              <div v-if="!item.form.enabled" class="agent-disabled-note">
                Mode ini tidak akan disimpan. Aktifkan toggle di atas jika ingin menambahkan pengaturan.
              </div>

              <template v-else>
              <agent-type-choice
                compact
                :value="item.form.header.ai_status"
                :data-agent-field="item.key + ':agentType'"
                @input="setFormAgent(item.form, $event)"
              />
              <small
                v-if="formFieldErrors[item.key] && formFieldErrors[item.key].agentType"
                class="agent-field-error"
              >
                {{ formFieldErrors[item.key].agentType }}
              </small>

              <div
                v-if="item.form.header.ai_status == 1"
                class="agent-ai-mode"
                :data-agent-field="item.key + ':aiMode'"
              >
                <button
                  type="button"
                  class="agent-ai-mode-card"
                  :class="{ active: item.form.header.ai_type === 'freestyle' }"
                  @click="item.form.header.ai_type = 'freestyle'"
                >
                  <span class="agent-radio" :class="{ checked: item.form.header.ai_type === 'freestyle' }"></span>
                  <strong>Chat Completions</strong>
                  <small>Pengetahuan umum + prompt</small>
                </button>
                <button
                  type="button"
                  class="agent-ai-mode-card"
                  :class="{ active: item.form.header.ai_type === 'summarize' }"
                  @click="item.form.header.ai_type = 'summarize'"
                >
                  <span class="agent-radio" :class="{ checked: item.form.header.ai_type === 'summarize' }"></span>
                  <strong>Knowledge Base</strong>
                  <small>Dokumen Knowledge Base lisensi</small>
                </button>
              </div>
              <small
                v-if="formFieldErrors[item.key] && formFieldErrors[item.key].aiMode"
                class="agent-field-error"
              >
                {{ formFieldErrors[item.key].aiMode }}
              </small>

              <div v-if="item.form.header.ai_status == 1 && item.form.header.ai_type === 'summarize'" class="agent-kb-inline">
                <div class="agent-kb-inline-head">
                  <strong>Knowledge Base</strong>
                  <span>{{ kbTotal }} dari {{ kbLimit }} KB</span>
                </div>
                <p>Dokumen Knowledge Base digunakan bersama oleh semua agent AI pada lisensi ini.</p>
                <agent-button variant="primary" size="sm" icon="fa fa-book" @click="openKbModal">
                  Kelola Knowledge Base
                </agent-button>
                <small
                  v-if="formFieldErrors[item.key] && formFieldErrors[item.key].knowledgeBase"
                  class="agent-field-error"
                >
                  {{ formFieldErrors[item.key].knowledgeBase }}
                </small>
                <div v-if="kbError" class="agent-kb-inline-error">{{ kbError }}</div>
                <div v-else-if="!kbLoading && !kbTotal" class="agent-kb-inline-warning">
                  Belum ada Knowledge Base untuk lisensi ini. Tambahkan dokumen agar AI dapat menggunakannya.
                </div>
              </div>
              </template>
            </div>
          </div>
        </section>

        <!-- Step 4 -->
        <section v-show="step === 4" class="agent-v2-card">
          <div class="agent-card-head">
            <div>
              <span class="agent-card-kicker">Tahap 4</span>
              <h4>Setting Pesan &amp; AI</h4>
              <p>Atur pesan, prompt, frekuensi, dan opsi sesi. Pengaturan tombol lama tetap dipertahankan saat Anda mengedit.</p>
            </div>
          </div>

          <div class="agent-settings-stack">
            <agent-config-form
              v-for="item in activeFormItems"
              :key="'settings-' + item.key"
              :ref="'agentForm-' + item.key"
              :config="item.form"
              :mode-label="item.modeLabel"
              :form-key="item.key"
              :license-id="selectedLicense ? selectedLicense.id : ''"
              :errors="formErrors[item.key] || []"
              :field-errors="formFieldErrors[item.key] || {}"
              :kb-rows="kbRows"
              :kb-total="kbTotal"
              :kb-limit="kbLimit"
              :kb-loading="kbLoading"
              :kb-error="kbError"
              :text-reply-rows="textReplyRows"
              :text-reply-loading="textReplyLoading"
              :text-reply-error="textReplyError"
              @reload-kb="loadKb"
              @reload-text-reply="loadTextReplies"
              @create-text-reply="openTextReplyCreate(item, $event)"
              @open-kb="openKbModal"
            />
          </div>
        </section>

        <!-- Step 5 -->
        <section v-show="step === 5" class="agent-v2-card">
          <div class="agent-card-head">
            <div>
              <span class="agent-card-kicker">Tahap 5</span>
              <h4>Ringkasan &amp; Aktivasi</h4>
              <p>Periksa pengaturan. Draft tidak akan aktif sampai Anda menekan Aktifkan Agent.</p>
            </div>
          </div>

          <div class="agent-summary-grid">
            <div class="agent-summary-box">
              <span>Lisensi</span>
              <strong>{{ selectedLicense ? (selectedLicense.license_name || "Tanpa nama") : "-" }}</strong>
              <small>{{ selectedLicense ? selectedLicense.license_phone_number : "" }}</small>
            </div>
            <div class="agent-summary-box">
              <span>Mode Respons</span>
              <strong>{{ selectedMode === "always" ? "Balas Otomatis 24 Jam" : "Berdasarkan Jam Kerja" }}</strong>
              <small>{{ selectedMode === "always" ? "Satu pengaturan sepanjang hari" : "Saat Jam Kerja + Di Luar Jam Kerja" }}</small>
            </div>
            <div class="agent-summary-box">
              <span>Status Saat Ini</span>
              <strong>{{ existingStatusLabel }}</strong>
              <small>{{ analysis.messages.length ? "Ada pengaturan yang bertabrakan" : "Tidak ada pengaturan yang bertabrakan" }}</small>
            </div>
          </div>

          <div class="agent-summary-configs">
            <div v-for="item in activeFormItems" :key="'summary-' + item.key" class="agent-summary-config">
              <div class="agent-summary-config-head">
                <div>
                  <span class="agent-mode-pill" :class="'is-' + modeClass(item.key)">{{ item.modeLabel }}</span>
                  <h5>{{ item.form.header.title || "Tanpa judul" }}</h5>
                </div>
                <span class="agent-status-pill" :class="Number(item.form.header.is_publish) === 1 ? 'is-active' : 'is-draft'">
                  {{ Number(item.form.header.is_publish) === 1 ? "Aktif" : "Draft" }}
                </span>
              </div>
              <div class="agent-summary-config-grid">
                <div><span>Jenis Agent</span><strong>{{ item.form.header.ai_status == 1 ? "Chat AI" : "Chatbot" }}</strong></div>
                <div v-if="item.form.header.ai_status == 1"><span>Mode AI</span><strong>{{ item.form.header.ai_type === "summarize" ? "Knowledge Base" : "Chat Completions" }}</strong></div>
                <div v-if="item.form.header.ai_status == 1 && item.form.header.ai_type === 'summarize'"><span>Knowledge Base</span><strong>{{ kbTotal }} dari {{ kbLimit }} KB</strong></div>
                <div v-if="item.form.header.ai_status == 0 && item.form.header.button_type === 'text_reply'"><span>Text Reply</span><strong>{{ textReplyCount(item.form) }} dipilih</strong></div>
                <div><span>Frekuensi</span><strong>{{ frequencyLabel(item.form.header.frequency_per_person) }}</strong></div>
                <div><span>Prompt AI</span><strong>{{ item.form.header.ai_status == 1 ? (item.form.header.ai_prompt_system ? "Diisi" : "Default") : "-" }}</strong></div>
                <div>
                  <span>Lampiran</span>
                  <strong>
                    {{
                      !item.form.header.is_enable_attach_file
                        ? "Tidak ada"
                        : (item.form.header.message_type === "image" ? "Gambar" : "Dokumen") +
                          (item.form.header.image_url ? "" : " (belum dipilih)")
                    }}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          <div v-if="activationBlocked" class="agent-alert is-warning">
            <i class="fa fa-exclamation-triangle"></i>
            <div>
              <strong>Aktivasi belum dapat dilakukan</strong>
              <div v-for="(message, index) in activationBlockedMessages" :key="index">{{ message }}</div>
              <div>Anda tetap dapat menyimpan sebagai draft, lalu menyelesaikan konflik dari halaman daftar agent.</div>
              <div class="agent-error-actions">
                <agent-button variant="secondary" size="sm" @click="goToStepForError">
                  Kembali ke Pengaturan
                </agent-button>
                <agent-button
                  v-if="workingHoursBlocked"
                  variant="primary"
                  size="sm"
                  icon="fa fa-clock-o"
                  @click="openWorkingHours"
                >
                  Atur Jam Kerja
                </agent-button>
                <agent-button
                  v-else
                  variant="primary"
                  size="sm"
                  icon="fa fa-exclamation-triangle"
                  @click="openConflictPage"
                >
                  Buka Detail Konflik
                </agent-button>
              </div>
            </div>
          </div>

          <div v-if="saveError" class="agent-alert is-danger">
            <i class="fa fa-exclamation-circle"></i>
            <div>
              <span>{{ saveError }}</span>
              <div class="agent-error-actions">
                <agent-button variant="secondary" size="sm" @click="goToStepForError">
                  Kembali ke Pengaturan
                </agent-button>
                <agent-button
                  v-if="activationConflict"
                  variant="primary"
                  size="sm"
                  icon="fa fa-exclamation-triangle"
                  @click="openConflictPage"
                >
                  Buka Detail Konflik
                </agent-button>
              </div>
            </div>
          </div>
        </section>

        <div class="agent-wizard-footer">
          <button type="button" class="btn btn-outline-secondary" :disabled="step === 1 || saving" @click="prevStep">
            <i class="fa fa-arrow-left"></i> Kembali
          </button>
          <div class="agent-wizard-actions">
            <button v-if="step < 5" type="button" class="btn agent-btn-primary" :disabled="saving" @click="nextStep">
              Lanjut <i class="fa fa-arrow-right"></i>
            </button>
            <template v-else>
              <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="saveDraft">
                <i v-if="saving" class="fa fa-spinner fa-spin"></i>
                <i v-else class="fa fa-save"></i>
                Simpan Draft
              </button>
              <button
                type="button"
                class="btn agent-btn-primary"
                :disabled="saving || activationBlocked"
                @click="activateAgent"
              >
                <i v-if="saving" class="fa fa-spinner fa-spin"></i>
                <i v-else class="fa fa-check"></i>
                Aktifkan Agent
              </button>
            </template>
          </div>
        </div>

        <knowledge-base-modal
          :show="kbModalOpen"
          :license="selectedLicense"
          :limit="kbLimit"
          @close="closeKbModal"
          @changed="onKbChanged"
        />

        <text-reply-create-modal
          :show="textReplyCreateOpen"
          :saving="textReplyCreateSaving"
          :error="textReplyCreateError"
          @clear-error="textReplyCreateError = ''"
          @close="closeTextReplyCreate"
          @save="saveTextReply"
        />
      </template>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import AgentConfigForm from "./components/AgentConfigForm.vue";
import KnowledgeBaseModal from "./components/KnowledgeBaseModal.vue";
import TextReplyCreateModal from "./components/TextReplyCreateModal.vue";
import AgentButton from "./components/AgentButton.vue";
import AgentTypeChoice from "./components/AgentTypeChoice.vue";
import ToggleSwitch from "./components/ToggleSwitch.vue";
import {
  MODE_ALWAYS,
  MODE_WORKING,
  MODE_AWAY,
  MODE_OPTIONS,
  FREQUENCY_OPTIONS,
  createForm,
  normalizeForm,
  modeFromConfig,
  modeLabel,
  analyzeConfigs,
  licenseMode,
  buildConfigPayload,
  validateFormDetailed,
  agentTypeLabel,
  aiModeLabel,
  mapApiError,
  friendlyError
} from "./utils";
import {
  normalizeWorkingHours,
  validateWorkingHours,
  workingHoursSummary
} from "./workingHours";
import {
  AGENT_V2_DRAFT_KEY,
  serializeWizardDraft,
  parseWizardDraft
} from "./wizardDraft";

require("vue-multiselect/dist/vue-multiselect.min.css");

export default {
  name: "AgentAiV2Wizard",
  components: {
    Multiselect,
    AgentConfigForm,
    KnowledgeBaseModal,
    TextReplyCreateModal,
    AgentButton,
    AgentTypeChoice,
    ToggleSwitch
  },
  data() {
    return {
      steps: [
        { number: 1, title: "Pilih Lisensi", subtitle: "Nomor WhatsApp" },
        { number: 2, title: "Pilih Mode", subtitle: "24 jam atau jam kerja" },
        { number: 3, title: "Pengaturan Agent", subtitle: "Chatbot atau Chat AI" },
        { number: 4, title: "Pesan & AI", subtitle: "Prompt dan pengaturan" },
        { number: 5, title: "Simpan & Aktifkan", subtitle: "Ringkasan pengaturan" }
      ],
      modeOptions: MODE_OPTIONS,
      step: 1,
      loading: false,
      licensesLoading: false,
      kbLoading: false,
      saving: false,
      loadError: "",
      wizardError: "",
      saveError: "",
      licenses: [],
      selectedLicense: null,
      existingConfigs: [],
      selectedMode: MODE_ALWAYS,
      forms: {
        always: createForm(MODE_ALWAYS),
        working: createForm(MODE_WORKING),
        away: createForm(MODE_AWAY)
      },
      formErrors: {},
      kbRows: [],
      kbTotal: 0,
      kbLimit: 5,
      kbError: "",
      kbModalOpen: false,
      aiBalance: null,
      textReplyRows: [],
      textReplyLoading: false,
      textReplyError: "",
      validationErrors: [],
      formFieldErrors: {},
      saveErrorStep: 5,
      saveErrorField: "",
      activationConflict: false,
      textReplyCreateOpen: false,
      textReplyCreateSaving: false,
      textReplyCreateError: "",
      textReplyCreateTarget: null,
      workingHoursData: null,
      workingHoursLoading: false,
      workingHoursError: "",
      workingHoursLoaded: false,
      workingHoursSeq: 0
    };
  },
  computed: {
    isEdit() {
      return !!this.$route.query.id;
    },
    isScheduleMode() {
      return this.selectedMode === "schedule";
    },
    selectionFormItems() {
      if (this.isScheduleMode) {
        return [
          { key: MODE_WORKING, modeLabel: modeLabel(MODE_WORKING), form: this.forms.working },
          { key: MODE_AWAY, modeLabel: modeLabel(MODE_AWAY), form: this.forms.away }
        ];
      }
      return [{ key: MODE_ALWAYS, modeLabel: modeLabel(MODE_ALWAYS), form: this.forms.always }];
    },
    activeFormItems() {
      return this.selectionFormItems.filter(item => item.form.enabled);
    },
    workingHoursReadiness() {
      return validateWorkingHours(this.workingHoursData || {});
    },
    workingHoursSummaryRows() {
      if (!this.workingHoursReadiness.isValid) return [];
      return workingHoursSummary(this.workingHoursData);
    },
    analysis() {
      return analyzeConfigs(this.existingConfigs);
    },
    modeConflictMessage() {
      if (!this.existingConfigs.length) return "";
      if (this.selectedMode === "always") {
        if ((this.analysis.activeCounts[MODE_WORKING] || 0) > 0 || (this.analysis.activeCounts[MODE_AWAY] || 0) > 0) {
          return "Saat ini ada pengaturan Saat Jam Kerja/Di Luar Jam Kerja yang aktif. Nonaktifkan terlebih dahulu sebelum mengaktifkan mode 24 Jam.";
        }
      } else if ((this.analysis.activeCounts[MODE_ALWAYS] || 0) > 0) {
        return "Saat ini ada pengaturan 24 Jam yang aktif. Nonaktifkan terlebih dahulu sebelum mengaktifkan mode jam kerja.";
      }
      return "";
    },
    workingHoursBlocked() {
      return (
        this.isScheduleMode &&
        !this.workingHoursLoading &&
        (!!this.workingHoursError || !this.workingHoursReadiness.isValid)
      );
    },
    activationBlockedMessages() {
      const messages = [];
      this.analysis.messages.forEach(item => messages.push(item));
      if (this.modeConflictMessage) messages.push(this.modeConflictMessage);
      if (this.isScheduleMode && !this.workingHoursLoading) {
        if (this.workingHoursError) {
          messages.push("Jam kerja belum berhasil dimuat. Periksa jadwal jam kerja terlebih dahulu.");
        } else if (!this.workingHoursReadiness.isValid) {
          messages.push(
            "Jam kerja belum diatur atau belum valid. Atur hari dan jam operasional sebelum mengaktifkan agent berbasis jam kerja."
          );
        }
      }
      return messages;
    },
    activationBlocked() {
      return this.activationBlockedMessages.length > 0;
    },
    existingStatusLabel() {
      if (!this.existingConfigs.length) return "Belum Diatur";
      if (this.analysis.hasConflict) return "Konflik";
      if (this.existingConfigs.some(row => Number(row.is_publish) === 1)) return "Aktif";
      return "Draft";
    }
  },
  watch: {
    "$route.query": {
      deep: true,
      handler() {
        if (this.$route.name !== "agentAiV2Create" && this.$route.name !== "agentAiV2Edit") return;
        this.resetForRoute();
        this.loadInitial();
      }
    }
  },
  created() {
    this.loadInitial();
  },
  methods: {
    resetForRoute() {
      this.step = 1;
      this.selectedLicense = null;
      this.existingConfigs = [];
      this.selectedMode = MODE_ALWAYS;
      this.forms = {
        always: createForm(MODE_ALWAYS),
        working: createForm(MODE_WORKING),
        away: createForm(MODE_AWAY)
      };
      this.formErrors = {};
      this.kbRows = [];
      this.kbTotal = 0;
      this.kbError = "";
      this.textReplyError = "";
      this.validationErrors = [];
      this.formFieldErrors = {};
      this.saveErrorStep = 5;
      this.saveErrorField = "";
      this.activationConflict = false;
      this.textReplyCreateOpen = false;
      this.textReplyCreateSaving = false;
      this.textReplyCreateError = "";
      this.textReplyCreateTarget = null;
      this.resetWorkingHoursState();
      this.wizardError = "";
      this.saveError = "";
    },
    resetWorkingHoursState() {
      this.workingHoursData = null;
      this.workingHoursLoading = false;
      this.workingHoursError = "";
      this.workingHoursLoaded = false;
      this.workingHoursSeq += 1;
    },
    licenseLabel(option) {
      if (!option) return "";
      return (option.license_name || "Tanpa nama") + " - " + (option.license_phone_number || "-");
    },
    modeFromConfig,
    modeLabel,
    modeClass(mode) {
      if (mode === MODE_ALWAYS) return "always";
      if (mode === MODE_WORKING) return "working";
      if (mode === MODE_AWAY) return "away";
      return "none";
    },
    agentLabel(row) {
      return agentTypeLabel(row) + (Number(row.ai_status) === 1 ? " / " + aiModeLabel(row) : "");
    },
    frequencyLabel(value) {
      const found = FREQUENCY_OPTIONS.find(item => Number(item.value) === Number(value));
      return found ? found.label : "-";
    },
    textReplyCount(form) {
      if (!form || Number(form.header.ai_status) === 1 || form.header.button_type !== "text_reply") {
        return 0;
      }
      return (form.detail_button || []).filter(row => row && row.auto_reply_button_id).length;
    },
    async loadInitial() {
      this.loading = true;
      this.loadError = "";
      try {
        await this.loadLicenses();
        await this.loadAiBalance();
        await this.loadTextReplies();
        const restored = await this.restoreWizardDraft();
        if (!restored) {
          this.clearWizardDraft();
          await this.applyRoute();
        }
        if (this.selectedMode === "schedule") {
          await this.loadWorkingHours(true);
        }
      } catch (error) {
        this.loadError = friendlyError(error, "Data agent belum berhasil dimuat. Silakan coba lagi.");
      } finally {
        this.loading = false;
      }
    },
    async loadLicenses() {
      this.licensesLoading = true;
      try {
        const response = await this.$store.dispatch("license/getLicenseList", {
          license_name: "",
          license_phone_number: "",
          license_status: "",
          page: 0,
          perPage: 200
        });
        this.licenses = (response.data && response.data.data) || [];
      } finally {
        this.licensesLoading = false;
      }
    },
    async loadAiBalance() {
      try {
        const response = await this.$store.dispatch("aiKey/getBalance");
        this.aiBalance = response.data || null;
      } catch (error) {
        this.aiBalance = { is_available: false };
      }
    },
    async loadTextReplies() {
      this.textReplyLoading = true;
      this.textReplyError = "";
      try {
        const response = await this.$store.dispatch("automation/listTextReplySilent", {
          columnFilters: {
            id: "",
            title: "",
            is_publish: "publish"
          },
          page: 0,
          perPage: 500
        });
        this.textReplyRows = (response.data && response.data.data) || [];
      } catch (error) {
        this.textReplyRows = [];
        this.textReplyError = friendlyError(
          error,
          "Daftar Text Reply belum berhasil dimuat. Silakan coba lagi."
        );
      } finally {
        this.textReplyLoading = false;
      }
    },
    async restoreWizardDraft() {
      if (String(this.$route.query.restore || "") !== "1") return false;
      let raw = "";
      try {
        raw = window.sessionStorage.getItem(AGENT_V2_DRAFT_KEY);
      } catch (error) {
        return false;
      }
      const draft = parseWizardDraft(raw);
      if (!draft) return false;
      if (this.$route.query.license_id && this.$route.query.license_id !== draft.licenseId) {
        return false;
      }
      if (
        this.$route.query.id &&
        draft.configId &&
        this.$route.query.id !== draft.configId
      ) {
        return false;
      }
      const license = this.licenses.find(item => item.id === draft.licenseId);
      if (!license) return false;

      const always = normalizeForm(draft.forms.always, MODE_ALWAYS);
      const working = normalizeForm(draft.forms.working, MODE_WORKING);
      const away = normalizeForm(draft.forms.away, MODE_AWAY);
      always.enabled = draft.forms.always.enabled !== false;
      working.enabled = draft.forms.working.enabled !== false;
      away.enabled = draft.forms.away.enabled !== false;
      always.header.license_id = license.id;
      working.header.license_id = license.id;
      away.header.license_id = license.id;

      this.selectedLicense = license;
      this.selectedMode = draft.mode;
      this.step = draft.step;
      this.forms = { always: always, working: working, away: away };
      await this.loadConfigs(license.id, false);
      await this.loadKb();
      return true;
    },
    persistWizardDraft() {
      if (!this.selectedLicense) return false;
      try {
        window.sessionStorage.setItem(
          AGENT_V2_DRAFT_KEY,
          serializeWizardDraft({
            licenseId: this.selectedLicense.id,
            configId: this.$route.query.id || "",
            mode: this.selectedMode === "schedule" ? "schedule" : MODE_ALWAYS,
            step: this.step,
            forms: this.forms
          })
        );
        return true;
      } catch (error) {
        return false;
      }
    },
    clearWizardDraft() {
      try {
        window.sessionStorage.removeItem(AGENT_V2_DRAFT_KEY);
      } catch (error) {
        // sessionStorage dapat diblokir browser; tidak mengubah alur simpan.
      }
    },
    async loadWorkingHours(force) {
      if (!force && this.workingHoursLoaded) return;
      const seq = this.workingHoursSeq + 1;
      this.workingHoursSeq = seq;
      this.workingHoursLoading = true;
      this.workingHoursError = "";
      try {
        const response = await this.$store.dispatch("toolkit/detailWorkingHours");
        if (seq !== this.workingHoursSeq) return;
        this.workingHoursData = normalizeWorkingHours(response.data || {});
        this.workingHoursLoaded = true;
      } catch (error) {
        if (seq !== this.workingHoursSeq) return;
        this.workingHoursData = null;
        this.workingHoursLoaded = false;
        this.workingHoursError = friendlyError(
          error,
          "Jadwal jam kerja belum berhasil dimuat. Silakan coba lagi."
        );
      } finally {
        if (seq === this.workingHoursSeq) {
          this.workingHoursLoading = false;
        }
      }
    },
    async openWorkingHours() {
      if (!this.selectedLicense) {
        this.step = 1;
        this.wizardError = "Pilih lisensi WhatsApp terlebih dahulu.";
        return;
      }

      const saved = this.persistWizardDraft();
      if (!saved) {
        const confirm = await this.$fire({
          title: "Isian belum dapat disimpan sementara",
          text:
            "Browser tidak mengizinkan penyimpanan isian sementara. Jika melanjutkan, isian yang belum disimpan dapat hilang. Lanjutkan ke pengaturan jam kerja?",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Tetap lanjut",
          cancelButtonText: "Batal"
        });
        if (!confirm || !confirm.value) return;
      }

      const returnQuery = {
        license_id: this.selectedLicense.id,
        mode: "schedule",
        restore: "1",
        step: String(this.step)
      };
      if (this.$route.query.id) returnQuery.id = this.$route.query.id;
      const returnPath = this.$router.resolve({
        path: this.$route.path,
        query: returnQuery
      }).route.fullPath;

      this.$router.push({
        path: "/toolkit/working-hours/list",
        query: { back: returnPath }
      });
    },
    async applyRoute() {
      const query = this.$route.query || {};
      let license = null;
      if (query.license_id) {
        license = this.licenses.find(item => item.id === query.license_id) || null;
      }
      if (query.id) {
        const detail = await this.fetchDetail(query.id);
        if (detail) {
          const mode = modeFromConfig(detail.header);
          if (license === null) {
            license = this.licenses.find(item => item.id === detail.header.license_id) || null;
          }
          if (license) {
            this.selectedLicense = license;
            this.selectedMode = mode === MODE_ALWAYS ? MODE_ALWAYS : "schedule";
            await this.loadConfigs(license.id, false);
            await this.loadKb();
            await this.loadFormsForMode(true);
          }
        }
        return;
      }
      if (license) {
        this.selectedLicense = license;
        await this.loadConfigs(license.id, false);
        await this.loadKb();
        this.selectedMode = query.mode === "schedule" ? "schedule" : (query.mode === "always" ? MODE_ALWAYS : (licenseMode(this.existingConfigs) || MODE_ALWAYS));
        await this.loadFormsForMode(false);
      }
    },
    async onLicenseChange(license) {
      this.wizardError = "";
      this.saveError = "";
      this.existingConfigs = [];
      this.kbRows = [];
      this.kbTotal = 0;
      this.kbError = "";
      this.validationErrors = [];
      this.formFieldErrors = {};
      this.saveErrorStep = 5;
      this.saveErrorField = "";
      this.activationConflict = false;
      this.textReplyCreateOpen = false;
      this.textReplyCreateError = "";
      this.textReplyCreateTarget = null;
      this.resetWorkingHoursState();
      this.forms = {
        always: createForm(MODE_ALWAYS),
        working: createForm(MODE_WORKING),
        away: createForm(MODE_AWAY)
      };
      this.step = 1;
      if (!license) return;
      try {
        await this.loadConfigs(license.id, true);
        await this.loadKb();
        const queryMode = this.$route.query.mode;
        this.selectedMode = queryMode === "schedule" ? "schedule" : (queryMode === "always" ? MODE_ALWAYS : (licenseMode(this.existingConfigs) || MODE_ALWAYS));
        await this.loadFormsForMode(false);
        if (this.selectedMode === "schedule") {
          await this.loadWorkingHours(true);
        }
      } catch (error) {
        this.wizardError = friendlyError(error, "Pengaturan lisensi belum berhasil dimuat. Silakan coba lagi.");
      }
    },
    async loadConfigs(licenseId, resetForms) {
      const response = await this.$store.dispatch("automation/listSmartReply", {
        columnFilters: {
          license_id: licenseId,
          title: "",
          message: "",
          trigger_on: "",
          is_working_or_away: "",
          is_working: ""
        },
        page: 0,
        perPage: 200
      });
      this.existingConfigs = ((response.data && response.data.data) || []).filter(
        row => row.trigger_on === "all"
      );
      if (resetForms) {
        this.forms = {
          always: createForm(MODE_ALWAYS),
          working: createForm(MODE_WORKING),
          away: createForm(MODE_AWAY)
        };
      }
    },
    async fetchDetail(id) {
      try {
        const response = await this.$store.dispatch("automation/detailSmartReplyForEdit", { id: id });
        return response.data || null;
      } catch (error) {
        this.wizardError = friendlyError(error, "Detail pengaturan belum berhasil dimuat. Silakan coba lagi.");
        return null;
      }
    },
    async loadFormsForMode(force) {
      if (!this.selectedLicense) return;
      const licenseId = this.selectedLicense.id;
      const preferredId = this.$route.query.id || "";
      if (this.selectedMode === "always") {
        const row =
          this.existingConfigs.find(item => item.id === preferredId && modeFromConfig(item) === MODE_ALWAYS) ||
          this.existingConfigs.find(item => modeFromConfig(item) === MODE_ALWAYS);
        if (row) {
          const detail = await this.fetchDetail(row.id);
          if (detail) this.forms.always = normalizeForm(detail, MODE_ALWAYS);
        } else {
          this.forms.always = createForm(MODE_ALWAYS);
        }
        this.forms.always.enabled = true;
      } else {
        const working =
          this.existingConfigs.find(item => item.id === preferredId && modeFromConfig(item) === MODE_WORKING) ||
          this.existingConfigs.find(item => modeFromConfig(item) === MODE_WORKING);
        const away =
          this.existingConfigs.find(item => item.id === preferredId && modeFromConfig(item) === MODE_AWAY) ||
          this.existingConfigs.find(item => modeFromConfig(item) === MODE_AWAY);
        if (working) {
          const detail = await this.fetchDetail(working.id);
          if (detail) this.forms.working = normalizeForm(detail, MODE_WORKING);
        } else {
          this.forms.working = createForm(MODE_WORKING);
          // Jika hanya pengaturan Luar Jam Kerja yang ada, jangan paksa
          // membuat pengaturan Jam Kerja baru.
          this.forms.working.enabled = !away;
        }
        if (away) {
          const detail = await this.fetchDetail(away.id);
          if (detail) this.forms.away = normalizeForm(detail, MODE_AWAY);
        } else {
          this.forms.away = createForm(MODE_AWAY);
          this.forms.away.enabled = !working;
        }
        this.forms.working.enabled = working ? true : this.forms.working.enabled;
        this.forms.away.enabled = away ? true : this.forms.away.enabled;
      }
      if (force) {
        this.forms.always.header.license_id = licenseId;
        this.forms.working.header.license_id = licenseId;
        this.forms.away.header.license_id = licenseId;
      }
    },
    async onModeChange(mode) {
      if (this.selectedMode === mode) return;
      this.selectedMode = mode;
      this.formErrors = {};
      this.validationErrors = [];
      this.formFieldErrors = {};
      this.saveError = "";
      this.activationConflict = false;
      await this.loadFormsForMode(false);
      if (mode === "schedule") {
        await this.loadWorkingHours(true);
      }
    },
    setFormAgent(form, type) {
      form.header.ai_status = type;
      if (type === 1) {
        form.header.is_enable_attach_file = false;
        form.header.image_url = "";
        form.header.message_type = "text";
        if (!form.header.id || !form.header.ai_type) form.header.ai_type = "summarize";
      } else {
        form.header.ai_close_status = 0;
        form.header.ai_loading = 0;
        form.header.is_ai_create_summary = 0;
      }
    },
    openKbModal() {
      if (!this.selectedLicense) {
        this.wizardError = "Pilih lisensi WhatsApp terlebih dahulu.";
        return;
      }
      this.kbModalOpen = true;
    },
    closeKbModal() {
      this.kbModalOpen = false;
    },
    onKbChanged() {
      this.loadKb();
    },
    async loadKb() {
      if (!this.selectedLicense) return;
      this.kbLoading = true;
      this.kbError = "";
      try {
        const response = await this.$store.dispatch("aiKey/historyListKB", {
          page: 0,
          perPage: 50,
          columnFilters: { license_id: this.selectedLicense.id }
        });
        const data = (response.data && response.data.data) || [];
        this.kbRows = data.slice(0, 5);
        this.kbTotal = Number(response.data && response.data.totalRecords) || data.length;
      } catch (error) {
        this.kbRows = [];
        this.kbTotal = 0;
        this.kbError = friendlyError(error, "Knowledge Base belum berhasil dimuat. Silakan coba lagi.");
      } finally {
        this.kbLoading = false;
      }
    },
    openTextReplyCreate(item, index) {
      if (this.textReplyCreateSaving || !item) return;
      this.textReplyCreateTarget = { item: item, index: index };
      this.textReplyCreateError = "";
      this.textReplyCreateOpen = true;
    },
    closeTextReplyCreate() {
      if (this.textReplyCreateSaving) return;
      this.textReplyCreateOpen = false;
      this.textReplyCreateError = "";
      this.textReplyCreateTarget = null;
    },
    async saveTextReply(payload) {
      if (this.textReplyCreateSaving) return;
      this.textReplyCreateSaving = true;
      this.textReplyCreateError = "";
      try {
        const response = await this.$store.dispatch("automation/storeTextReplySilent", {
          header: {
            id: "",
            title: payload.title,
            message: payload.message,
            is_enable_attach_file: false,
            is_custom_url: false,
            image_url: "",
            message_type: "text",
            is_publish: 1,
            tag_id: JSON.stringify([]),
            tag_id_notif: JSON.stringify([])
          }
        });
        let created = response.data || {};
        if (!created.id) {
          await this.loadTextReplies();
          created =
            this.textReplyRows.find(
              row => String(row.title || "") === String(payload.title || "")
            ) || {};
        }
        if (!created.id) {
          this.textReplyCreateOpen = false;
          this.textReplyCreateTarget = null;
          this.wizardError =
            "Text Reply berhasil dibuat, tetapi daftar belum dapat diperbarui. Muat ulang halaman.";
          return;
        }

        const option = {
          id: String(created.id),
          title: created.title || payload.title,
          button_title: created.title || payload.title,
          button_text: created.title || payload.title,
          message: created.message || payload.message,
          is_publish: created.is_publish != null ? created.is_publish : 1
        };
        this.textReplyRows = this.textReplyRows
          .filter(row => String(row.id) !== option.id)
          .concat([option]);

        const target = this.textReplyCreateTarget;
        this.textReplyCreateOpen = false;
        this.textReplyCreateTarget = null;
        if (target && target.item) {
          let formComp = this.$refs["agentForm-" + target.item.key];
          if (Array.isArray(formComp)) formComp = formComp[0];
          if (formComp && typeof formComp.selectTextReplyOption === "function") {
            formComp.selectTextReplyOption(target.index, option);
          } else {
            const rows = (target.item.form.detail_button || []).slice();
            rows[target.index] = {
              auto_reply_button_id: option.id,
              button_type: "text_reply"
            };
            target.item.form.detail_button = rows.filter(
              row => row && row.auto_reply_button_id
            );
            target.item.form.header.button_type = "text_reply";
          }
        }
        this.$fire({
          title: "Berhasil",
          text: "Text Reply berhasil dibuat dan dipilih.",
          type: "success",
          timer: 1600
        });
      } catch (error) {
        const mapped = mapApiError(
          error,
          "Text Reply belum berhasil dibuat. Periksa kembali isian atau coba lagi."
        );
        this.textReplyCreateError =
          mapped.field === "message"
            ? "Isi pesan Text Reply wajib diisi."
            : mapped.field === "title"
              ? "Judul Text Reply wajib diisi."
              : mapped.message;
      } finally {
        this.textReplyCreateSaving = false;
      }
    },
    firstActiveKey() {
      return this.activeFormItems.length ? this.activeFormItems[0].key : "";
    },
    focusAgentField(error) {
      if (!error) return;
      if (error.step) this.step = error.step;
      const key = error.key || this.firstActiveKey();
      if (!error.field || !key) return;
      this.$nextTick(() => {
        const selector =
          '[data-agent-field="' + key + ":" + error.field + '"]';
        const container = document.querySelector(selector);
        if (!container) return;
        if (container.scrollIntoView) {
          container.scrollIntoView({ block: "center", behavior: "smooth" });
        }
        const focusable =
          container.matches && container.matches("input,textarea,select,button")
            ? container
            : container.querySelector("input,textarea,select,button");
        if (focusable && focusable.focus) {
          focusable.focus({ preventScroll: true });
        }
      });
    },
    focusValidationError(error) {
      if (!error) return;
      this.wizardError = error.message;
      this.focusAgentField(error);
    },
    validateForms() {
      const errors = [];
      const formErrors = {};
      const fieldErrors = {};
      this.activeFormItems.forEach(item => {
        const result = validateFormDetailed(item.form).map(err =>
          Object.assign({ key: item.key, modeLabel: item.modeLabel }, err)
        );
        formErrors[item.key] = result.map(err => err.message);
        fieldErrors[item.key] = {};
        result.forEach(err => {
          if (!fieldErrors[item.key][err.field]) {
            fieldErrors[item.key][err.field] = err.message;
          }
        });
        errors.push.apply(errors, result);
      });
      this.formErrors = formErrors;
      this.formFieldErrors = fieldErrors;
      this.validationErrors = errors;
      if (errors.length) {
        this.wizardError =
          errors.length === 1
            ? errors[0].message
            : errors[0].message + " (" + errors.length + " masalah)";
        this.focusAgentField(errors[0]);
        return false;
      }
      this.wizardError = "";
      return true;
    },
    goToStepForError() {
      const step = this.workingHoursBlocked
        ? 2
        : this.saveErrorStep && this.saveErrorStep < 5
          ? this.saveErrorStep
          : this.saveErrorField
            ? 4
            : this.activationBlocked
              ? 3
              : 4;
      this.step = step;
      if (this.saveErrorField) {
        this.focusAgentField({
          key: this.firstActiveKey(),
          field: this.saveErrorField,
          step: step
        });
      }
    },
    openConflictPage() {
      if (!this.selectedLicense) return;
      const resolved = this.$router.resolve({
        path: "/agent-ai-v2",
        query: { conflict: "1", license_id: this.selectedLicense.id }
      });
      const opened = window.open(resolved.href, "_blank");
      if (!opened) {
        this.wizardError =
          "Popup diblokir browser. Buka halaman daftar agent lalu pilih Detail Konflik.";
      }
    },
    validateCurrentStep() {
      this.wizardError = "";
      if (this.step === 1 && !this.selectedLicense) {
        this.validationErrors = [];
        this.wizardError = "Pilih lisensi WhatsApp terlebih dahulu.";
        return false;
      }
      if (this.step === 2 && !this.selectedMode) {
        this.validationErrors = [];
        this.wizardError = "Pilih mode respons terlebih dahulu.";
        return false;
      }
      if (this.step === 3 && this.activeFormItems.length === 0) {
        this.validationErrors = [];
        this.wizardError = "Pilih minimal satu mode yang akan disertakan.";
        return false;
      }
      if (this.step === 3) {
        const invalid = this.activeFormItems.find(
          item => [0, 1].indexOf(Number(item.form.header.ai_status)) === -1
        );
        if (invalid) {
          const error = {
            key: invalid.key,
            modeLabel: invalid.modeLabel,
            field: "agentType",
            message: "Silakan pilih jenis agent.",
            step: 3
          };
          this.validationErrors = [error];
          this.formFieldErrors = Object.assign({}, this.formFieldErrors, {
            [invalid.key]: { agentType: error.message }
          });
          this.wizardError = error.message;
          this.focusAgentField(error);
          return false;
        }
        this.validationErrors = [];
      }
      if (this.step === 4) {
        return this.validateForms();
      }
      return true;
    },
    nextStep() {
      if (!this.validateCurrentStep()) return;
      this.validationErrors = [];
      this.formFieldErrors = {};
      this.wizardError = "";
      if (this.step < 5) this.step += 1;
    },
    prevStep() {
      this.wizardError = "";
      this.validationErrors = [];
      this.formFieldErrors = {};
      if (this.step > 1) this.step -= 1;
    },
    payloadConfigs() {
      return this.activeFormItems.map(item => buildConfigPayload(item.form, this.selectedLicense.id));
    },
    async saveDraft() {
      const activeForms = this.activeFormItems.filter(
        item => item.form.header.id && Number(item.form.header.is_publish) === 1
      );
      if (activeForms.length > 0) {
        const confirm = await this.$fire({
          title: "Simpan sebagai draft?",
          text: "Pengaturan yang sedang aktif akan berubah menjadi draft dan berhenti merespons sampai diaktifkan kembali.",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Ya, simpan draft",
          cancelButtonText: "Batal"
        });
        if (!confirm.value) return;
      }
      await this.save(false);
    },
    async activateAgent() {
      if (this.activationBlocked) return;
      await this.save(true);
    },
    async save(activate) {
      if (this.saving) return;
      this.saveError = "";
      this.wizardError = "";
      this.saveErrorStep = 5;
      this.saveErrorField = "";
      this.activationConflict = false;
      if (!this.selectedLicense) {
        this.step = 1;
        this.wizardError = "Pilih lisensi WhatsApp terlebih dahulu.";
        return;
      }
      if (this.activeFormItems.length === 0) {
        this.step = 3;
        this.wizardError = "Pilih minimal satu mode yang akan disertakan.";
        return;
      }
      if (activate && this.isScheduleMode && !this.workingHoursReadiness.isValid) {
        this.step = 2;
        this.wizardError = this.workingHoursError
          ? "Jam kerja belum berhasil dimuat. Periksa jadwal jam kerja terlebih dahulu."
          : "Jam kerja belum diatur atau belum valid. Atur hari dan jam operasional sebelum mengaktifkan agent berbasis jam kerja.";
        return;
      }
      if (!this.validateForms()) return;

      this.saving = true;
      try {
        const response = await this.$store.dispatch("automation/storeAgentV2", {
          license_id: this.selectedLicense.id,
          configs: this.payloadConfigs(),
          activate: !!activate
        });
        const saved = response && Array.isArray(response.data) ? response.data : [];
        if (!saved.length) {
          this.saveError =
            "Penyimpanan selesai, tetapi data konfigurasi terbaru tidak diterima server. Buka daftar lalu muat ulang.";
          this.saveErrorStep = 5;
          return;
        }
        this.$fire({
          title: "Berhasil",
          text: activate ? "Agent berhasil disimpan dan diaktifkan." : "Pengaturan berhasil disimpan sebagai draft.",
          type: "success",
          timer: 2000
        });
        this.clearWizardDraft();
        // Daftar Agent V2 memakai keep-alive dan akan memuat ulang data pada
        // hook activated() saat kembali ke /agent-ai-v2.
        this.$router.push("/agent-ai-v2");
      } catch (error) {
        const fallback = activate
          ? "Agent belum berhasil diaktifkan. Periksa kembali pengaturan atau coba lagi."
          : "Draft belum berhasil disimpan. Periksa kembali pengaturan atau coba lagi.";
        const mapped = mapApiError(error, fallback);
        this.saveError = mapped.message;
        this.saveErrorStep = mapped.step || 5;
        this.saveErrorField = mapped.field || "";
        this.activationConflict = !!mapped.conflict;
        if (mapped.workingHours) {
          this.saveError = "";
          this.step = 2;
          this.wizardError = mapped.message;
          await this.loadWorkingHours(true);
          return;
        }

        if (mapped.conflict || !mapped.field) {
          this.step = mapped.step === 1 ? 1 : 5;
          if (mapped.step === 1) this.wizardError = mapped.message;
          return;
        }

        const first = this.activeFormItems[0] || {};
        const key = first.key || "";
        const errorItem = {
          key: key,
          modeLabel: first.modeLabel || "",
          field: mapped.field,
          message: mapped.message,
          step: mapped.step || 4
        };
        this.validationErrors = [errorItem];
        this.formErrors = Object.assign({}, this.formErrors, {
          [key]: (this.formErrors[key] || []).concat([mapped.message])
        });
        this.formFieldErrors = Object.assign({}, this.formFieldErrors, {
          [key]: Object.assign({}, this.formFieldErrors[key], {
            [mapped.field]: mapped.message
          })
        });
        this.wizardError = mapped.message;
        this.focusAgentField(errorItem);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.agent-v2-page {
  color: #344054;
  padding-bottom: 92px;
}
.agent-v2-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.agent-v2-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  color: #7b8ba3;
  font-size: 12px;
  font-weight: 700;
}
.agent-v2-heading h3 {
  margin: 0 0 4px;
  color: #172033;
  font-size: 26px;
  font-weight: 800;
}
.agent-v2-heading p {
  margin: 0;
  color: #7b8ba3;
  font-size: 13px;
}
.agent-v2-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  background: #fff4e8;
  color: #e77f25;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 800;
}
.agent-stepper {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}
.agent-step {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  border: 1px solid #e7eaf0;
  border-radius: 13px;
  background: #fff;
  padding: 11px 12px;
}
.agent-step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f2f4f7;
  color: #667085;
  font-size: 12px;
  font-weight: 800;
}
.agent-step strong {
  display: block;
  color: #344054;
  font-size: 12px;
  font-weight: 800;
}
.agent-step small {
  display: block;
  margin-top: 2px;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.35;
}
.agent-step.active {
  border-color: #fe9b44;
  background: #fff9f3;
}
.agent-step.active .agent-step-number {
  background: #fe9b44;
  color: #fff;
}
.agent-step.done .agent-step-number {
  background: #e7f8ef;
  color: #0b8f4d;
}
.agent-v2-card {
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  background: #fff;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, .035);
}
.agent-card-head {
  margin-bottom: 16px;
}
.agent-card-kicker {
  display: inline-block;
  color: #e77f25;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}
.agent-card-head h4 {
  margin: 4px 0 3px;
  color: #172033;
  font-size: 20px;
  font-weight: 800;
}
.agent-card-head p {
  margin: 0;
  color: #7b8ba3;
  font-size: 12px;
}
.agent-license-picker {
  max-width: 620px;
}
.agent-license-picker > label {
  display: block;
  margin-bottom: 7px;
  color: #344054;
  font-size: 12px;
  font-weight: 800;
}
.agent-license-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.agent-license-option strong {
  display: block;
  color: #344054;
  font-size: 12px;
}
.agent-license-option small {
  display: block;
  color: #667085;
  font-size: 12px;
}
.agent-license-picker >>> .multiselect__content-wrapper {
  max-height: 280px;
  border-color: #e7eaf0;
  box-shadow: 0 8px 24px rgba(16, 24, 40, .12);
  z-index: 100;
}
.agent-license-picker >>> .multiselect__option {
  background: #fff;
  color: #344054;
  transition: background-color .12s ease, color .12s ease;
}
.agent-license-picker >>> .multiselect__option--highlight {
  background: #fff8ef !important;
  color: #344054 !important;
}
.agent-license-picker >>> .multiselect__option--highlight::after {
  background: #fe9b44;
  color: #fff;
}
.agent-license-picker >>> .multiselect__option--selected {
  background: #ffefdc !important;
  color: #344054 !important;
  font-weight: 700;
}
.agent-license-picker >>> .multiselect__option--selected::after {
  color: #b45309;
}
.agent-license-picker >>> .multiselect__option--selected.multiselect__option--highlight {
  background: #ffe7cc !important;
  color: #344054 !important;
}
.agent-license-picker >>> .multiselect__option--selected.multiselect__option--highlight::after {
  background: #fe9b44;
  color: #fff;
}
.agent-license-picker >>> .multiselect__option--selected .agent-license-option strong,
.agent-license-picker >>> .multiselect__option--highlight .agent-license-option strong {
  color: #172033;
}
.agent-license-picker >>> .multiselect__option--selected .agent-license-option small,
.agent-license-picker >>> .multiselect__option--highlight .agent-license-option small {
  color: #667085;
}
.agent-connection {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 800;
}
.agent-connection i {
  font-size: 12px;
}
.agent-connection.is-online {
  color: #0b8f4d;
}
.agent-connection.is-offline {
  color: #d92d20;
}
.agent-selected-license {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 10px;
  border-radius: 10px;
  background: #f8fafc;
  padding: 9px 11px;
  color: #667085;
  font-size: 12px;
}
.agent-selected-license strong {
  color: #344054;
}
.agent-info-note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-top: 12px;
  border: 1px solid #dce9ff;
  border-radius: 10px;
  background: #f5f9ff;
  color: #56708f;
  padding: 10px 11px;
  font-size: 12px;
  line-height: 1.55;
}
.agent-info-note i {
  margin-top: 2px;
  color: #2e90fa;
}
.agent-mode-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.agent-mode-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  border: 1px solid #e7eaf0;
  border-radius: 14px;
  background: #fff;
  padding: 16px;
  cursor: pointer;
}
.agent-mode-card.active {
  border-color: #fe9b44;
  background: #fff9f3;
  box-shadow: 0 0 0 1px rgba(254, 155, 68, .15);
}
.agent-mode-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-bottom: 9px;
  border-radius: 10px;
  background: #fff4e8;
  color: #e77f25;
  font-size: 16px;
}
.agent-mode-card strong {
  color: #344054;
  font-size: 14px;
  font-weight: 800;
}
.agent-mode-card small {
  margin-top: 4px;
  color: #7b8ba3;
  font-size: 12px;
  line-height: 1.5;
}
.agent-mode-sub {
  margin-top: 9px;
  border-radius: 999px;
  background: #f2f4f7;
  color: #667085;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 700;
}
.agent-radio {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 16px;
  height: 16px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
}
.agent-radio.checked {
  border-color: #fe9b44;
  background: #fe9b44;
  box-shadow: inset 0 0 0 3px #fff;
}
.agent-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.6;
}
.agent-alert.is-warning {
  border: 1px solid #fde3b7;
  background: #fffaf0;
  color: #8a5a00;
}
.agent-alert.is-danger {
  border: 1px solid #ffd6d6;
  background: #fff7f7;
  color: #c02626;
}
.agent-validation-summary {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
  border: 1px solid #ffd6d6;
  border-radius: 10px;
  background: #fffafa;
  padding: 11px 12px;
}
.agent-validation-summary > strong {
  color: #b42318;
  font-size: 12px;
}
.agent-validation-item {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  border: 0;
  background: transparent;
  color: #b42318;
  padding: 0;
  text-align: left;
  font-size: 12px;
  line-height: 1.45;
  cursor: pointer;
}
.agent-validation-item span {
  flex: 0 0 auto;
  border-radius: 999px;
  background: #fff0f0;
  color: #b42318;
  padding: 2px 6px;
  font-size: 12px;
  font-weight: 800;
}
.agent-validation-item:hover {
  text-decoration: underline;
}
.agent-field-error {
  display: block;
  margin-top: 4px;
  color: #d92d20;
  font-size: 12px;
  line-height: 1.4;
}
.agent-error-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 10px;
}
.agent-existing-box {
  margin-top: 16px;
  border-top: 1px solid #f0f2f6;
  padding-top: 14px;
}
.agent-existing-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
  color: #344054;
  font-size: 12px;
}
.agent-existing-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid #f3f5f8;
  color: #475467;
  font-size: 12px;
}
.agent-existing-type {
  margin-left: auto;
  color: #98a2b3;
  font-size: 12px;
}
.agent-mode-pill,
.agent-status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 800;
}
.agent-mode-pill.is-always {
  background: #fff1e5;
  color: #e77f25;
}
.agent-mode-pill.is-working {
  background: #e8f2ff;
  color: #1769d1;
}
.agent-mode-pill.is-away {
  background: #f1eaff;
  color: #7f56d9;
}
.agent-mode-pill.is-none {
  background: #f2f4f7;
  color: #667085;
}
.agent-status-pill.is-active {
  background: #e7f8ef;
  color: #0b8f4d;
}
.agent-status-pill.is-draft {
  background: #fff4e5;
  color: #b76e00;
}
.agent-agent-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.agent-agent-grid.is-single {
  grid-template-columns: minmax(0, 1fr);
}
.agent-agent-card {
  border: 1px solid #e7eaf0;
  border-radius: 14px;
  background: #fbfcfe;
  padding: 15px;
}
.agent-agent-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.agent-agent-card-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.agent-include-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: #7b8ba3;
  font-size: 12px;
  font-weight: 700;
}
.agent-agent-card.is-disabled {
  background: #f7f8fa;
  opacity: .78;
}
.agent-disabled-note {
  border: 1px dashed #d8dee9;
  border-radius: 9px;
  background: #fff;
  color: #98a2b3;
  padding: 10px;
  font-size: 12px;
  line-height: 1.5;
}
.agent-agent-card-head h5 {
  margin: 3px 0 0;
  color: #344054;
  font-size: 14px;
  font-weight: 800;
}
.agent-ai-mode {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}
.agent-ai-mode-card {
  position: relative;
  text-align: left;
  border: 1px solid #e7eaf0;
  border-radius: 11px;
  background: #fff;
  padding: 11px;
  cursor: pointer;
}
.agent-ai-mode-card strong {
  display: block;
  color: #344054;
  font-size: 12px;
}
.agent-ai-mode-card small {
  display: block;
  margin-top: 2px;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.35;
}
.agent-ai-mode-card.active {
  border-color: #fe9b44;
  background: #fff8f1;
}
.agent-ai-mode-card .agent-radio {
  position: static;
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-bottom: 6px;
}
.agent-kb-inline {
  margin-top: 10px;
  border: 1px solid #dce9ff;
  border-radius: 10px;
  background: #f5f9ff;
  padding: 11px;
}
.agent-kb-inline-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #344054;
  font-size: 12px;
}
.agent-kb-inline p {
  margin: 5px 0 8px;
  color: #7b8ba3;
  font-size: 12px;
  line-height: 1.5;
}
.agent-kb-inline-error {
  margin-top: 7px;
  color: #d92d20;
  font-size: 12px;
}
.agent-kb-inline-warning {
  margin-top: 7px;
  color: #b76e00;
  font-size: 12px;
}
.agent-settings-stack > * + * {
  margin-top: 16px;
}
.agent-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.agent-summary-box {
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  background: #fbfcfe;
  padding: 12px;
}
.agent-summary-box span {
  display: block;
  color: #98a2b3;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
.agent-summary-box strong {
  display: block;
  margin-top: 3px;
  color: #344054;
  font-size: 13px;
}
.agent-summary-box small {
  display: block;
  margin-top: 2px;
  color: #7b8ba3;
  font-size: 12px;
}
.agent-summary-configs {
  margin-top: 14px;
}
.agent-summary-config {
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  background: #fff;
  padding: 13px;
}
.agent-summary-config + .agent-summary-config {
  margin-top: 10px;
}
.agent-summary-config-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.agent-summary-config-head h5 {
  margin: 6px 0 0;
  color: #344054;
  font-size: 14px;
  font-weight: 800;
}
.agent-summary-config-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 11px;
  padding-top: 11px;
  border-top: 1px solid #f1f4f8;
}
.agent-summary-config-grid span {
  display: block;
  color: #98a2b3;
  font-size: 12px;
}
.agent-summary-config-grid strong {
  display: block;
  margin-top: 2px;
  color: #475467;
  font-size: 12px;
}
.agent-working-hours {
  margin-top: 16px;
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  background: #fbfcfe;
  padding: 14px;
}
.agent-working-hours-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 11px;
}
.agent-working-hours-head h5 {
  margin: 2px 0 3px;
  color: #344054;
  font-size: 14px;
  font-weight: 800;
}
.agent-working-hours-head p {
  margin: 0;
  color: #7b8ba3;
  font-size: 12px;
  line-height: 1.45;
}
.agent-working-hours-badge {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 5px;
  border-radius: 999px;
  background: #f2f4f7;
  color: #667085;
  padding: 5px 9px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}
.agent-working-hours-badge.is-valid {
  background: #e8f7ee;
  color: #0b8f4d;
}
.agent-working-hours-badge.is-invalid {
  background: #fff1ed;
  color: #d92d20;
}
.agent-working-hours-state {
  display: flex;
  align-items: center;
  gap: 7px;
  border-radius: 9px;
  background: #fff;
  color: #667085;
  padding: 10px 11px;
  font-size: 12px;
}
.agent-working-hours-alert {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  border: 1px solid #f7d9a8;
  border-radius: 10px;
  background: #fffbf3;
  color: #7a4b08;
  padding: 11px;
  font-size: 12px;
  line-height: 1.5;
}
.agent-working-hours-alert.is-error {
  border-color: #f6c9c3;
  background: #fff8f7;
  color: #9d2318;
}
.agent-working-hours-alert > i {
  margin-top: 2px;
}
.agent-working-hours-alert strong {
  display: block;
  margin-bottom: 2px;
  color: inherit;
  font-size: 12px;
  font-weight: 800;
}
.agent-working-hours-alert p {
  margin: 0;
  color: inherit;
}
.agent-working-hours-errors {
  margin: 7px 0 0;
  padding-left: 17px;
}
.agent-working-hours-errors li {
  margin: 2px 0;
}
.agent-working-hours-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 9px;
}
.agent-working-hours-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
}
.agent-working-hours-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid #edf1f6;
  border-radius: 9px;
  background: #fff;
  padding: 8px 10px;
  color: #475467;
  font-size: 12px;
}
.agent-working-hours-row strong {
  color: #172033;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}
.agent-working-hours-row strong.is-off {
  color: #98a2b3;
  font-weight: 700;
}
.agent-working-hours-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
}
.agent-working-hours-foot small {
  color: #7b8ba3;
  font-size: 12px;
  line-height: 1.4;
}
.agent-wizard-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
}
.agent-wizard-actions {
  display: flex;
  gap: 8px;
}
.agent-btn-primary {
  border: 0;
  background: #fe9b44;
  color: #fff;
  font-weight: 800;
}
.agent-btn-primary:hover,
.agent-btn-primary:focus {
  background: #e77f25;
  color: #fff;
}
.agent-v2-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  background: #fff;
  padding: 40px 20px;
  color: #667085;
  font-size: 13px;
}
.agent-v2-state.is-error {
  color: #c02626;
}
.agent-helper {
  display: block;
  margin-top: 4px;
  color: #98a2b3;
  font-size: 12px;
}
.agent-helper.is-danger {
  color: #d92d20;
}
@media (max-width: 1199px) {
  .agent-stepper {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 991px) {
  .agent-mode-grid,
  .agent-agent-grid,
  .agent-summary-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .agent-summary-config-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 767px) {
  .agent-v2-heading {
    flex-direction: column;
  }
  .agent-v2-heading h3 {
    font-size: 22px;
  }
  .agent-stepper {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .agent-v2-card {
    padding: 14px;
  }
  .agent-ai-mode {
    grid-template-columns: minmax(0, 1fr);
  }
  .agent-working-hours-head,
  .agent-working-hours-foot {
    flex-direction: column;
    align-items: flex-start;
  }
  .agent-working-hours-summary {
    grid-template-columns: minmax(0, 1fr);
  }
  .agent-working-hours-actions .btn {
    width: 100%;
  }
  .agent-wizard-footer {
    flex-direction: column;
    align-items: stretch;
  }
  .agent-wizard-actions {
    flex-direction: column;
  }
  .agent-wizard-actions .btn {
    width: 100%;
  }
}
@media (max-width: 480px) {
  .agent-stepper {
    grid-template-columns: minmax(0, 1fr);
  }
  .agent-summary-config-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ai/agentV2/wizard.vue